import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  TrashIcon,
  UserIcon,
  PlusIcon
} from '@heroicons/react/24/outline';
import { X, Crown } from 'lucide-react';
import { dashboardApiClient, TeamMember, PaginatedResponse } from '../services/api';
import { DataTable, DataTableColumn, DataTableAction, Pagination, SearchInput, ActiveFilter, WorkspacesStack, ConfirmationModal, Button, PageHeader, useDataTableUrl } from '@airclou/ui';
import { TeamMemberFiltersPopover, TeamMemberFilters, TEAM_MEMBER_STATUSES, TEAM_MEMBER_ROLES } from '../components/TeamMemberFiltersPopover';
import { TABLE_CONFIG } from '@airclou/shared';
import { useTimezone } from '@airclou/shell/timezone'
import { formatDateInTz } from '@airclou/utils'

interface App {
  id: string;
  name: string;
  slug: string;
  description?: string;
}

interface GroupedTeamMember {
  user_id: string;
  name: string;
  email: string;
  first_name?: string;
  last_name?: string;
  avatar_url?: string;
  last_login?: string;
  workspaces: Array<{
    workspace_id: string;
    workspace_name: string;
    workspace_slug: string;
    workspace_role: 'admin' | 'member';
  }>;
  primary_role: string;
  primary_status: 'active' | 'inactive' | 'suspended' | 'pending_verification';
  latest_joined_at: string;
  latest_updated_at: string;
  is_tenant_admin: boolean;
  // Mirrors TeamMember.tenant_role: the org role straight from `tenant_users`.
  // It was narrower because the API used to overwrite the creator's role with
  // 'admin'; it now reports what the row holds, `owner` included.
  tenant_role: 'owner' | 'admin' | 'billing_admin' | 'viewer' | 'user';
}

interface MembersPageProps {
  hideHeader?: boolean;
  /** Opens the page-level invite modal; owned by the parent (see PeoplePage). */
  onInviteClick?: () => void;
  /** Bumped by the parent after an invite lands, to re-run the member fetch. */
  refreshKey?: number;
}

export function MembersPage({ hideHeader = false, onInviteClick, refreshKey = 0 }: MembersPageProps) {
  const timezone = useTimezone()
  const { t } = useTranslation('dashboard');
  const [teamData, setTeamData] = useState<PaginatedResponse<TeamMember> | null>(null);
  const [groupedTeamData, setGroupedTeamData] = useState<GroupedTeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [apps, setApps] = useState<App[]>([]);
  const [isFiltersPopoverOpen, setIsFiltersPopoverOpen] = useState(false);

  // The filters live in the URL beside sort/search/page, so a filtered list
  // survives a reload too. 'all' is the absent param, and a value the popover
  // doesn't offer (a stale or hand-edited link) reads as 'all' too. The app list
  // is loaded, not fixed, so an unknown app id is left to the API.
  const [searchParams] = useSearchParams();
  const rawStatus = searchParams.get('status');
  const rawRole = searchParams.get('role');
  const statusParam = rawStatus && TEAM_MEMBER_STATUSES.includes(rawStatus) ? rawStatus : 'all';
  const roleParam = rawRole && TEAM_MEMBER_ROLES.includes(rawRole) ? rawRole : 'all';
  const appParam = searchParams.get('app') || 'all';
  const filters: TeamMemberFilters = useMemo(
    () => ({ status: statusParam, role: roleParam, app: appParam }),
    [statusParam, roleParam, appParam],
  );
  // Sort, search and page live in the URL (same pattern as the audit log), so a
  // sorted or filtered list survives a reload, a shared link and back/forward.
  // The URL carries the column id; the fetch maps it to the backend field below.
  const {
    search: searchQuery,
    page,
    limit: pageSize,
    sorting,
    onSortingChange: handleSortingChange,
    onSearchChange: handleSearchChange,
    onPageChange,
    clampPage,
    setParams,
  } = useDataTableUrl({ defaultLimit: TABLE_CONFIG.DEFAULT_PAGE_SIZE });

  // One URL write per change, through the hook so it skips a write that changes
  // nothing and returns to page 1 on one that does. `clearSearch` folds the
  // search reset into the same write, so Clear all is a single history entry.
  const setFilters = useCallback((next: TeamMemberFilters, clearSearch = false) => {
    setParams({
      status: next.status !== 'all' ? next.status : null,
      role: next.role !== 'all' ? next.role : null,
      app: next.app !== 'all' ? next.app : null,
      ...(clearSearch && { search: null }),
    });
  }, [setParams]);

  // Confirmation modal state
  const [showRemoveConfirmation, setShowRemoveConfirmation] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState<{ id: string; name: string } | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);

  const loadTeamApps = async () => {
    try {
      const response = await dashboardApiClient.getTeamApps();
      if (response) {
        setApps(response);
      }
    } catch (err) {
      console.error('Failed to load apps:', err);
    }
  };

  // Which load is the current one. Sort, search, page and filters each start a
  // load, so a slow answer can land after a newer one; only the newest may set
  // the rows or move the URL's page (a stale answer's page count would clamp a
  // page that is perfectly valid for what the reader is now looking at).
  const loadSeq = useRef(0);

  const loadTeamMembers = useCallback(async () => {
    const seq = ++loadSeq.current;
    try {
      setLoading(true);
      setError(null);

      let sortField = 'joined_at';
      let sortOrder = 'desc';

      if (sorting.length > 0) {
        const sort = sorting[0];
        // Mapped here rather than through the hook's `columnToSortField` on
        // purpose: `member` and `joined` both sort on `joined_at`, and the hook
        // inverts its map to pick the header to highlight for `?sortBy=`, so
        // one of the two headers would show the other's sort.
        const fieldMap: Record<string, string> = {
          'member': 'joined_at',
          'app': 'app_id',
          'role': 'role',
          'status': 'status',
          'joined': 'joined_at'
        };

        sortField = fieldMap[sort.id] || 'joined_at';
        sortOrder = sort.desc ? 'desc' : 'asc';
      }

      const response = await dashboardApiClient.getTeamMembers({
        page,
        limit: pageSize,
        search: searchQuery || undefined,
        role: filters.role !== 'all' ? filters.role : undefined,
        status: filters.status !== 'all' ? filters.status : undefined,
        app_id: filters.app !== 'all' ? filters.app : undefined,
        sort: sortField,
        order: sortOrder
      });
      if (seq !== loadSeq.current) return;

      if (response && response.data) {
        setTeamData(response);
        setGroupedTeamData(groupTeamMembersByUser(response.data));
        // A stale link, or removing the last member on the last page, leaves the
        // URL past the end; the server answers with an empty page. Move to the
        // last real page, which refetches.
        if (response.meta) clampPage(response.meta.totalPages);
      } else {
        setError(t('people.failedToLoadMembers'));
      }
    } catch (err) {
      if (seq !== loadSeq.current) return;
      console.error('Failed to load team members:', err);
      setError(t('people.failedToLoadMembers'));
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, [page, pageSize, searchQuery, filters.role, filters.status, filters.app, sorting, clampPage]);

  useEffect(() => {
    loadTeamApps();
  }, []);

  useEffect(() => {
    loadTeamMembers();
  }, [loadTeamMembers, refreshKey]);

  const handleStatusUpdate = async (memberId: string, newStatus: string) => {
    try {
      await dashboardApiClient.updateTeamMemberStatus(memberId, newStatus);
      await loadTeamMembers();
    } catch (err) {
      console.error('Failed to update status:', err);
      setError(t('teamMember.failedToUpdateStatus'));
    }
  };

  const handleRemoveMemberClick = (memberId: string, memberName: string) => {
    setMemberToRemove({ id: memberId, name: memberName });
    setShowRemoveConfirmation(true);
  };

  const handleRemoveMember = async () => {
    if (!memberToRemove) return;

    setIsRemoving(true);
    try {
      await dashboardApiClient.removeTeamMember(memberToRemove.id);
      setShowRemoveConfirmation(false);
      setMemberToRemove(null);
      await loadTeamMembers();
    } catch (err) {
      console.error('Failed to remove member:', err);
      setError(t('people.failedToRemove'));
    } finally {
      setIsRemoving(false);
    }
  };

  const handleClearFilters = useCallback(() => {
    setFilters({ status: 'all', role: 'all', app: 'all' }, true);
  }, [setFilters]);

  const getActiveFiltersCount = useMemo(() => {
    let count = 0;
    if (filters.status !== 'all') count++;
    if (filters.role !== 'all') count++;
    if (filters.app !== 'all') count++;
    return count;
  }, [filters]);

  const getActiveFilters = useMemo((): ActiveFilter[] => {
    const activeFilters: ActiveFilter[] = [];

    if (searchQuery) {
      activeFilters.push({
        key: 'search',
        label: `"${searchQuery}"`,
        value: searchQuery,
        onRemove: () => handleSearchChange('')
      });
    }

    if (filters.status !== 'all') {
      activeFilters.push({
        key: 'status',
        label: `${t('common.status')}: ${filters.status}`,
        value: filters.status,
        onRemove: () => setFilters({ ...filters, status: 'all' })
      });
    }

    if (filters.role !== 'all') {
      activeFilters.push({
        key: 'role',
        label: `${t('common.role')}: ${filters.role}`,
        value: filters.role,
        onRemove: () => setFilters({ ...filters, role: 'all' })
      });
    }

    if (filters.app !== 'all') {
      const appName = apps.find(app => app.id === filters.app)?.name || filters.app;
      activeFilters.push({
        key: 'app',
        label: `${t('apps.app')}: ${appName}`,
        value: filters.app,
        onRemove: () => setFilters({ ...filters, app: 'all' })
      });
    }

    return activeFilters;
  }, [searchQuery, filters, setFilters, apps, t, handleSearchChange]);

  const activeFilters = getActiveFilters;

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      active: { label: t('common.active'), color: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200' },
      inactive: { label: t('common.inactive'), color: 'bg-muted text-foreground' },
      suspended: { label: t('common.suspended'), color: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200' },
      pending_verification: { label: t('common.pending'), color: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200' }
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.inactive;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const groupedColumns: DataTableColumn<GroupedTeamMember>[] = useMemo(() => [
    {
      id: 'member',
      header: t('common.member'),
      accessorKey: 'name',
      enableSorting: true,
      size: 320,
      cell: (groupedMember: GroupedTeamMember) => {
        const isPlaceholderEmail = groupedMember.email?.endsWith('@pending.setup');
        const displayEmail = isPlaceholderEmail ? null : (groupedMember.email || null);

        return (
          <div>
            <div className="flex items-center gap-1 text-sm font-medium text-foreground">
              {groupedMember.name || t('organization.unknownUser')}
              {groupedMember.primary_role === 'owner' && (
                <span className="relative group">
                  <Crown className="ml-1 h-4 w-4 text-yellow-500" aria-label={t('common.owner')} />
                  <span className="absolute left-1/2 -translate-x-1/2 mt-1 z-10 whitespace-nowrap rounded bg-gray-900 px-2 py-1 text-xs text-white opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                    {t('common.owner')}
                  </span>
                </span>
              )}
            </div>
            {displayEmail && (
              <div className="text-sm text-gray-500 dark:text-gray-400">
                {displayEmail}
              </div>
            )}
            {!displayEmail && (
              <div className="text-sm text-gray-400 dark:text-gray-500 italic">
                {t('people.emailNotAvailable')}
              </div>
            )}
          </div>
        );
      },
    },
    {
      id: 'workspaces',
      header: t('common.workspaces'),
      accessorKey: 'workspaces',
      enableSorting: false,
      cell: (groupedMember: GroupedTeamMember) => {
        return (
          <WorkspacesStack
            workspaces={groupedMember.workspaces.map(ws => ({
              id: ws.workspace_id,
              name: ws.workspace_name,
              slug: ws.workspace_slug,
              role: ws.workspace_role
            }))}
            maxVisible={3}
            size="xs"
            showTooltip={true}
          />
        );
      },
    },
    {
      id: 'status',
      header: t('common.status'),
      accessorKey: 'primary_status',
      enableSorting: true,
      size: 90,
      cell: (groupedMember: GroupedTeamMember) => getStatusBadge(groupedMember.primary_status),
    },
    {
      id: 'joined',
      header: t('common.joined'),
      accessorKey: 'latest_joined_at',
      enableSorting: true,
      size: 110,
      cell: (groupedMember: GroupedTeamMember) => (
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {formatDateInTz(groupedMember.latest_joined_at, timezone)}
        </span>
      ),
    },
  ], [getStatusBadge, t, timezone]);

  const groupedActions: DataTableAction<GroupedTeamMember>[] = useMemo(() => [
    {
      id: 'toggle-status',
      label: t('people.toggleStatus'),
      icon: UserIcon,
      onClick: (groupedMember: GroupedTeamMember) => {
        handleStatusUpdate(
          groupedMember.user_id,
          groupedMember.primary_status === 'active' ? 'suspended' : 'active'
        );
      },
      disabled: (groupedMember: GroupedTeamMember) => groupedMember.is_tenant_admin,
    },
    {
      id: 'remove',
      label: t('people.removeUser'),
      icon: TrashIcon,
      onClick: (groupedMember: GroupedTeamMember) => {
        handleRemoveMemberClick(groupedMember.user_id, groupedMember.name || groupedMember.email);
      },
      variant: 'destructive',
      disabled: (groupedMember: GroupedTeamMember) => groupedMember.is_tenant_admin,
    },
  ], [handleStatusUpdate, handleRemoveMemberClick, t]);

  const groupedGlobalFilterFn = useCallback((groupedMember: GroupedTeamMember, searchQuery: string) => {
    const searchLower = searchQuery.toLowerCase();
    return (
      (groupedMember.name?.toLowerCase().includes(searchLower) ?? false) ||
      (groupedMember.email?.toLowerCase().includes(searchLower) ?? false) ||
      (groupedMember.tenant_role?.toLowerCase().includes(searchLower) ?? false) ||
      (groupedMember.primary_status?.toLowerCase().includes(searchLower) ?? false) ||
      (groupedMember.primary_role?.toLowerCase().includes(searchLower) ?? false)
    );
  }, []);

  const groupTeamMembersByUser = useCallback((members: TeamMember[]): GroupedTeamMember[] => {
    return members.map(member => ({
      user_id: member.user_id,
      name: member.name,
      email: member.email,
      first_name: member.first_name,
      last_name: member.last_name,
      avatar_url: member.avatar_url,
      last_login: member.last_login,
      workspaces: member.workspaces?.map(ws => ({
        workspace_id: ws.workspace_id,
        workspace_name: ws.workspace_name,
        workspace_slug: ws.workspace_slug,
        workspace_role: ws.workspace_role
      })) || [],
      primary_role: member.role,
      primary_status: member.status,
      latest_joined_at: member.joined_at,
      latest_updated_at: member.updated_at,
      is_tenant_admin: member.is_tenant_admin,
      tenant_role: member.tenant_role,
    }));
  }, []);

  return (
    <div className="space-y-4">
      {!hideHeader && (
        <PageHeader
          title={t('people.allMembers')}
          description={t('people.allMembersSubtitle')}
          // The wizard belongs to the parent, so only offer the button when a
          // parent is listening — a button wired to nothing is the bug this
          // whole change exists to fix.
          actions={
            onInviteClick && (
              <Button onClick={onInviteClick}>
                <PlusIcon className="w-4 h-4 mr-2" />
                {t('people.inviteMember')}
              </Button>
            )
          }
        />
      )}

      <div className="flex flex-col gap-2">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
          <div className="w-full max-w-sm">
            <TeamMemberFiltersPopover
              onFiltersChange={setFilters}
              filters={filters}
              isOpen={isFiltersPopoverOpen}
              onOpenChange={setIsFiltersPopoverOpen}
              apps={apps}
              trigger={
                <SearchInput
                  value={searchQuery}
                  onChange={handleSearchChange}
                  placeholder={t('people.searchTeamMembers')}
                  onClear={() => handleSearchChange('')}
                  showClearButton={true}
                  size="md"
                  showFiltersIcon={true}
                  filtersActive={getActiveFiltersCount > 0}
                  onFiltersClick={() => setIsFiltersPopoverOpen(true)}
                />
              }
            />
          </div>

          <div className="flex justify-end sm:justify-start">
            <Pagination
              currentPage={page}
              totalItems={teamData?.meta?.total || 0}
              itemsPerPage={teamData?.meta?.limit || pageSize}
              onPageChange={onPageChange}
            />
          </div>
        </div>

        {activeFilters.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-gray-600 dark:text-gray-400">{t('people.activeFilters')}</span>
            {activeFilters.map((filter) => (
              <div key={filter.key} className="flex items-center gap-1 px-2 py-1 bg-primary/15 dark:bg-primary/25 text-primary dark:text-[hsl(var(--primary-hover))] text-xs rounded-full">
                <span>{filter.label}</span>
                <button
                  onClick={filter.onRemove}
                  className="ml-1 hover:bg-primary/25 dark:hover:bg-primary/35 rounded-full p-0.5 transition-colors"
                >
                  <X className="h-2 w-2" />
                </button>
              </div>
            ))}
            <button
              onClick={handleClearFilters}
              className="ml-auto text-xs h-6 px-2 text-primary hover:text-[hsl(var(--primary-hover))] hover:bg-primary/10 dark:hover:bg-primary/15 rounded"
            >
              {t('people.clearAll')}
            </button>
          </div>
        )}
      </div>

      <div className="overflow-x-auto">
        <DataTable
          data={groupedTeamData || []}
          columns={groupedColumns}
          actions={groupedActions}
          searchQuery={searchQuery}
          sorting={sorting}
          onSortingChange={handleSortingChange}
          // Use user_id instead of app_users.id so the link survives app revocations.
          getRowHref={(groupedMember: GroupedTeamMember) => `/people/user/${groupedMember.user_id}`}
          isLoading={loading}
          error={error}
          onRetry={loadTeamMembers}
          globalFilterFn={groupedGlobalFilterFn}
          emptyMessage={t('people.noTeamMembersFound')}
          enablePagination={false}
        />
      </div>

      <ConfirmationModal
        isOpen={showRemoveConfirmation}
        onClose={() => {
          setShowRemoveConfirmation(false);
          setMemberToRemove(null);
        }}
        title={t('people.removeTeamMember')}
        description={
          memberToRemove
            ? t('people.removeTeamMemberConfirm', { name: memberToRemove.name })
            : t('people.removeTeamMemberDefault')
        }
        variant="danger"
        confirmText={t('common.remove')}
        cancelText={t('common.cancel')}
        onConfirm={handleRemoveMember}
        isLoading={isRemoving}
      />
    </div>
  );
}

export default MembersPage;
